import { useCallback, useMemo, useSyncExternalStore } from "react";
import {
  tagContentSchema,
  uuidv7,
  type ContentBlob,
  type Tag,
  type TagContent,
  type TaggableType,
} from "@vmeste/shared";
import { t, type MessageKey } from "../i18n/index.js";
import { operations } from "../lib/outbox.js";
import { selectTags, selectTagsFor } from "../lib/store.js";
import { syncEngine } from "../lib/sync.js";
import { plaintextOf } from "../lib/plaintext.js";

/**
 * Tags (SPEC §4.6, §7).
 *
 * A tag is a name shared by the space and a link saying what it is on, so the
 * same «подарки» is one row however many things wear it — renaming it renames
 * it everywhere, which is the whole reason tags are not just typed words.
 *
 * Like everything else on this side, mutations go through the outbox and show
 * immediately; nothing here waits for a round trip.
 */

/*
 * A tag's name is content like any other, so it is written with the space's own
 * codec (SPEC §3). `null` — encrypted here, no key on this device — means the
 * tag is not created or renamed at all, rather than written where anybody with
 * the database can read it.
 */

/* ------------------------------------------------------------------ colour */

/**
 * The palette a tag may be painted in.
 *
 * Token names, never hex. The stored value is a role in the palette declared in
 * `src/styles.css`, and that palette is redefined wholesale in the dark theme —
 * so a tag stays legible on paper in the light theme and legible on near-black
 * in the dark one. A stored `#8a6412` would be a choice made once, in one
 * theme, and unreadable in the other.
 *
 * The six names are historical and stay that way on purpose: they are in the
 * database. What each one *looks* like now comes from the `tag-*` family rather
 * than from the semantic tokens it is named after, because `warn` has to keep
 * meaning amber for a deadline while a tag called «зелёный» has to be green.
 * Sharing one set made that impossible; see the note in `styles.css`.
 */
export const TAG_COLORS = [
  "accent",
  "deep",
  "warn",
  "neg",
  "neutral",
  "plain",
] as const;

export type TagColor = (typeof TAG_COLORS)[number];

/** What a tag looks like when nobody picked. */
export const DEFAULT_TAG_COLOR: TagColor = "neutral";

/**
 * Chip classes per token. Every colour is a `@theme` token, so both themes work.
 *
 * Four hues at one lightness and one chroma, plus a filled one and an outline.
 * Equal weight is the point: a row of tags should read as a row, not as a
 * traffic light where the red one looks urgent and the grey one looks disabled.
 */
export const TAG_COLOR_CLASS: Record<TagColor, string> = {
  accent: "bg-tag-amber-soft text-tag-amber",
  // `text-ground` rather than `text-white`: ground is warm paper when the accent
  // is deep amber and near-black when it is bright amber, so the pair stays
  // legible either way.
  deep: "bg-accent text-ground",
  warn: "bg-tag-sage-soft text-tag-sage",
  neg: "bg-tag-rose-soft text-tag-rose",
  neutral: "bg-tag-slate-soft text-tag-slate",
  plain: "text-muted ring-1 ring-line",
};

/**
 * For the colour buttons in the picker — a screen reader needs words.
 *
 * Keys, not the words themselves. A table of words built at module scope is
 * built once, in whichever language was on when the module loaded, and stays
 * that way for the rest of the session; a table of keys is looked up at render,
 * so the label follows the language like everything else on the screen.
 */
export const TAG_COLOR_KEY: Record<TagColor, MessageKey> = {
  accent: "tags.colorAccent",
  deep: "tags.colorDeep",
  warn: "tags.colorWarn",
  neg: "tags.colorNeg",
  neutral: "tags.colorNeutral",
  plain: "tags.colorPlain",
};

/** The colour's name in the language on right now, for a caller without a `t`. */
export function tagColorLabel(color: TagColor): string {
  return t(TAG_COLOR_KEY[color]);
}

/** An unknown or missing token falls back rather than rendering nothing. */
export function tagColorOf(value: string | null | undefined): TagColor {
  return TAG_COLORS.includes(value as TagColor) ? (value as TagColor) : DEFAULT_TAG_COLOR;
}

/* ---------------------------------------------------------------- decoding */

/**
 * A tag as the screens render it: a name and a palette token, no blob.
 *
 * Same boundary as everywhere else — nothing below the codec sees a ContentBlob
 * (SPEC §3).
 */
export interface TagView {
  id: string;
  name: string;
  color: TagColor;
}

/**
 * Decode a tag's content for rendering.
 *
 * Synchronous because `none` mode carries plain JSON. An e2ee blob cannot be
 * read this way, and when that mode arrives the decode moves to where a delta is
 * applied — components still never see a ContentBlob.
 */
function readTagContent(blob: ContentBlob | undefined): TagContent | null {
  const value = plaintextOf(blob);
  if (value === undefined) return null;
  const parsed = tagContentSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

function toTagView(tag: Tag): TagView | null {
  const content = readTagContent(tag.content);
  if (!content) return null;
  return { id: tag.id, name: content.name, color: tagColorOf(tag.color) };
}

/** Alphabetical, in Russian collation — `ё` sorts where a reader expects it. */
function byName(a: TagView, b: TagView): number {
  return a.name.localeCompare(b.name, "ru");
}

const sameName = (a: string, b: string) =>
  a.trim().toLocaleLowerCase("ru") === b.trim().toLocaleLowerCase("ru");

/* -------------------------------------------------------------------- hook */

export interface TagActions {
  /** Every tag in the space, alphabetical. */
  tags: TagView[];
  /** The tags on one thing. */
  tagsOf(targetType: TaggableType, targetId: string): TagView[];
  create(name: string, color?: TagColor): void;
  /** Make a tag and put it on something in one gesture — the picker's case. */
  createOn(
    name: string,
    targetType: TaggableType,
    targetId: string,
    color?: TagColor,
  ): void;
  rename(tagId: string, name: string): void;
  recolour(tagId: string, color: TagColor): void;
  /** Soft delete. The server takes the links with it, under one revision. */
  remove(tagId: string): void;
  link(tagId: string, targetType: TaggableType, targetId: string): void;
  unlink(tagId: string, targetType: TaggableType, targetId: string): void;
}

export function useTags(spaceId: string | null): TagActions {
  const snapshot = useSyncExternalStore(syncEngine.subscribe, syncEngine.getSnapshot);

  const tags = useMemo(() => {
    if (!spaceId) return [];

    return selectTags(syncEngine.view(spaceId))
      .map(toTagView)
      .filter((tag): tag is TagView => tag !== null)
      .sort(byName);
    // The engine notifies on every change and this re-renders, so recomputing
    // per snapshot is exactly right.
  }, [spaceId, snapshot]);

  const tagsOf = useCallback(
    (targetType: TaggableType, targetId: string): TagView[] => {
      if (!spaceId) return [];

      return selectTagsFor(syncEngine.view(spaceId), targetType, targetId)
        .map(toTagView)
        .filter((tag): tag is TagView => tag !== null)
        .sort(byName);
    },
    [spaceId, snapshot],
  );

  /**
   * Create, and hand back the id it will have.
   *
   * The id is minted here rather than inside the operation because the caller
   * may want to link the tag in the same gesture, and the encode is async — by
   * the time the operation exists the link would have nothing to point at.
   */
  const mint = useCallback(
    (name: string, color: TagColor, afterQueued?: (id: string) => void): string | null => {
      if (!spaceId) return null;

      const trimmed = name.trim();
      if (!trimmed) return null;

      // Before the id is minted, so a caller never gets back an id for a tag
      // that is not going to exist — and never links anything to it.
      const codec = syncEngine.codec(spaceId);
      if (!codec) return null;

      const id = uuidv7();

      void codec.encode({ name: trimmed }).then((content) => {
        syncEngine.mutate(operations.createTag(spaceId, { id, content, color }));

        /**
         * Anything that depends on the tag existing runs here, not at the call
         * site.
         *
         * The encode is asynchronous, so a caller that queued a link straight
         * after `mint` returned put the link in front of the create. The outbox
         * drains in order, the server was asked to link a tag it had never
         * heard of, and the answer — not found — is permanent, so the link was
         * dropped and the tag silently attached to nothing.
         */
        afterQueued?.(id);
      });

      return id;
    },
    [spaceId],
  );

  const create = useCallback(
    (name: string, color: TagColor = DEFAULT_TAG_COLOR) => {
      mint(name, color);
    },
    [mint],
  );

  const link = useCallback(
    (tagId: string, targetType: TaggableType, targetId: string) => {
      if (!spaceId) return;
      syncEngine.mutate(operations.linkTag(spaceId, { tagId, targetType, targetId }));
    },
    [spaceId],
  );

  const createOn = useCallback(
    (
      name: string,
      targetType: TaggableType,
      targetId: string,
      color: TagColor = DEFAULT_TAG_COLOR,
    ) => {
      if (!spaceId) return;

      // Typing a name the space already has must not fork it in two. Two rows
      // called «подарки» look like one tag and behave like two, and nobody ever
      // finds out which of them is on what.
      const existing = tags.find((tag) => sameName(tag.name, name));

      if (existing) {
        link(existing.id, targetType, targetId);
        return;
      }

      // Linked only once the create is queued, so the two reach the server in
      // the order they depend on.
      mint(name, color, (id) => link(id, targetType, targetId));
    },
    [spaceId, tags, mint, link],
  );

  const rename = useCallback(
    (tagId: string, name: string) => {
      if (!spaceId) return;

      const trimmed = name.trim();
      if (!trimmed) return;

      const codec = syncEngine.codec(spaceId);
      if (!codec) return;

      void codec.encode({ name: trimmed }).then((content) => {
        syncEngine.mutate(operations.updateTag(spaceId, tagId, { content }));
      });
    },
    [spaceId],
  );

  const recolour = useCallback(
    (tagId: string, color: TagColor) => {
      if (!spaceId) return;
      syncEngine.mutate(operations.updateTag(spaceId, tagId, { color }));
    },
    [spaceId],
  );

  const remove = useCallback(
    (tagId: string) => {
      if (!spaceId) return;
      syncEngine.mutate(operations.deleteTag(spaceId, tagId));
    },
    [spaceId],
  );

  const unlink = useCallback(
    (tagId: string, targetType: TaggableType, targetId: string) => {
      if (!spaceId) return;
      syncEngine.mutate(operations.unlinkTag(spaceId, { tagId, targetType, targetId }));
    },
    [spaceId],
  );

  return { tags, tagsOf, create, createOn, rename, recolour, remove, link, unlink };
}
